import React from 'react';

import { PageProps } from '../../../components/types/coretypes';
import { FWorkbenchShell } from '../workbench';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InventoryIcon from '@mui/icons-material/Inventory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

import {
  BieOnboardingWorkbenchTab,
  BieRecordId,
  IBieOnboardingValidationIssue,
  IBieOnboardingValidationRule,
  IBieOnboardingWorkbench,
  IBieOnboardingWorkbenchFilters,
  IBieOnboardingWorkbenchImport,
  IBieLoadSessionPublishResult,
  IBieOnboardingWorkbenchState,
  emptyBieOnboardingWorkbenchFilters,
  emptyBieOnboardingWorkbenchState,
  getBieImportClassLabel,
} from './bieonboardingworkbench.models';

import {
  //BieOnboardingWorkbenchApi,
  //bieOnboardingWorkbenchApi,
  fetchBieOnboardingWorkbench,
  fetchBieOnboardingValidationIssues,
  fetchBieOnboardingValidationRules,
  resetBieOnboardingStaging,
} from './bieonboardingworkbench.apis';

import { BieOnboardingApiError } from './bieonboarding.api.error';
import BieOnboardingImportCard from './BieOnboardingImportCard';
import BieOnboardingIssueGrid from './BieOnboardingIssueGrid';
import BieImportDecisionDialog from './BieImportDecisionDialog';
import {
  BieImportDecisionCode,
  bulkSetBieImportDecisions,
  keepExactBieImportDuplicates,
  setBieImportDecision,
} from './bieimportdecision.apis';

import BieOnboardingOverview from './BieOnboardingOverview';
import BieOnboardingRuleGrid from './BieOnboardingRuleGrid';
import BieOnboardingStatusChip from './BieOnboardingStatusChip';
import { validateBieOnboardingReview } from './bieonboardingreview.apis';

import BieOnboardingWorkbenchStaging, {
  IBieOnboardingStagedImportSummary,
  IBieOnboardingWorkbenchStagingData,
} from './BieOnboardingWorkbenchStaging';
import { reviewBieOnboardingWorkbook } from './bieonboardingworkbook.apis';
import { publishBieLoadSession, stageBieLoadSession } from './bieloadsession.apis';
import BieOnboardingPublish from './BieOnboardingPublish';

import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import {
  reopenBieLoadSession,
} from './bieloadsession.apis';

import BieThresholdDecisionDialog from './BieThresholdDecisionDialog';
import { IBieGovernanceDecision, requestBieGovernanceDecision } from './biegovernancedecision.apis';

import { resolveAndValidateBieOnboardingReview } from './bieonboardingworkbench.apis';
import { IBieRenderedRemediation } from './bieonboarding.remediation';

import {
  fetchBieGovernanceDecisions,
  reviewBieGovernanceDecision,
  withdrawBieGovernanceDecision,
  //IBieGovernanceDecision,
  BieGovernanceReviewAction,
} from './biegovernancedecision.apis';

//import BieGovernanceDecisionGrid from './BieGovernanceDecisionGrid';
import { IBieOnboardingReviewValidateResult } from './bieonboardingreview.models';

/* ============================================================
 * Pages
 * ============================================================
 */

const PAGE_LOAD_SESSION_MANAGER = 'bieonboardingsession';
const PAGE_LOAD_SESSION_WIZARD = 'bieonboardingwizard';
const PAGE_IMPORT_RECORDS = 'bieonboardingimport';
const PAGE_IMPORT_ITEM = 'bieonboardingimportitem';

const PAGE_ACCOUNT_ALIAS = 'bieaccountalias';
const PAGE_DRIVER_ALIAS = 'biedriveralias';
const PAGE_LOCATION_ALIAS = 'bielocationalias';
const PAGE_PRODUCT_ALIAS = 'bieproductalias';
const PAGE_UNIT_ALIAS = 'bieunitalias';

const PAGE_ACCOUNT = 'bieaccount';
const PAGE_DRIVER = 'biedriver';
const PAGE_LOCATION = 'bielocation';
const PAGE_PRODUCT = 'bieproduct';
const PAGE_UNIT = 'bieunit';

const PAGE_DRIVER_THRESHOLD = 'biedriverthreshold';


type BieGovernanceUiAction =
  | BieGovernanceReviewAction
  | 'WITHDRAW';

/* ============================================================
 * Props
 * ============================================================
 */

export interface IBieOnboardingWorkbenchProps extends PageProps {
  loadSessionId?: BieRecordId;

  /**
   * Optional injectable API client.
   *
   * The default shared API client is used when omitted.
   */
  //api?: BieOnboardingWorkbenchApi;

  /**
   * Automatically loads the Workbench when mounted.
   *
   * Default: true
   */
  autoLoad?: boolean;

  /**
   * Optional page title.
   */
  //title?: string;

  /**
   * Optional navigation action.
   */
  onBack?: () => void;

  /**
   * Opens the staged records belonging to one Import.
   */
  onOpenImportRecords?: (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ) => void;

  /**
   * Opens one staged Import Item.
   */
  onOpenImportItem?: (
    importItemId: BieRecordId,
    issue?: IBieOnboardingValidationIssue
  ) => void;

  /**
   * Optional publication override retained for host compatibility.
   * When omitted, the Workbench calls the standard bieloadsession/publish API.
   */
  onPublish?: (
    loadSessionId: BieRecordId,
    stamp: string
  ) => Promise<void> | void;

  /**
   * Called whenever a newly refreshed Workbench contract is
   * received.
   */
  onWorkbenchChange?: (workbench: IBieOnboardingWorkbench) => void;

  // // Framework propsa
  // title?: string; // = { stitle }
  // sconfig?: any; // = { ServiceConfig }
  // sdata?: any; //  = { SData }
  // rows?: any[]; //  = { cm }
  // data?: any; //  = { data }
  // actionSet?: any; //  = { actionSet }
  // [name: string]: any; //{...others }
}

/* ============================================================
 * Component state
 * ============================================================
 */

// interface IBieOnboardingWorkbenchComponentState {
//   // Existing state...

  
// }

interface IBieOnboardingWorkbenchComponentState
  extends IBieOnboardingWorkbenchState {
  issuesLoading: boolean;
  rulesLoading: boolean;
  publishing: boolean;
  publicationResult: IBieLoadSessionPublishResult | null;

  validationDialogOpen: boolean;

  selectedImportId: BieRecordId | '';
  selectedIssueId: BieRecordId | '';
  selectedRuleId: BieRecordId | '';

  loadSessionId: BieRecordId;

  stagingReview:
  | IBieOnboardingWorkbenchStagingData
  | null;

  loadingStagingReview: boolean;
  refreshingStagingReview: boolean;

  stagingErrorMessage: string;

  staging: boolean;
  reopening: boolean;

  decisionDialogOpen: boolean;
  decisionIssue: IBieOnboardingValidationIssue | null;
  decisionCode: BieImportDecisionCode;
  decisionReason: string;
  decisionLoading: boolean;
  decisionErrorMessage: string;
  selectedDuplicateItemIds: BieRecordId[];

  thresholdDialogOpen: boolean;
  thresholdIssue: IBieOnboardingValidationIssue | null;
  thresholdReason: string;
  thresholdEvidence: string;
  thresholdLoading: boolean;
  thresholdErrorMessage: string;

  governanceDecisions: IBieGovernanceDecision[];

  resetDialogOpen: boolean;
  resettingStaging: boolean;

  selectedGovernanceDecisionId: BieRecordId | '';
  governanceStateFilter: string;

  governanceReviewDialogOpen: boolean;
  governanceReviewDecision: IBieGovernanceDecision | null;
  governanceReviewAction: BieGovernanceUiAction | '';
  governanceReviewNotes: string;
  governanceReviewLoading: boolean;
  governanceReviewErrorMessage: string;
}

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Main class component for the Pack 5 BIE Institutional
 * Onboarding Workbench.
 *
 * Coordinates:
 *
 * - Initial Workbench loading
 * - Overview dashboard
 * - Import worksheet cards
 * - Validation issue search and paging
 * - Validation rule catalogue
 * - Workbook validation and revalidation
 * - Pack 6 publication callback
 * - Navigation to staged records
 */
export default class BieOnboardingWorkbench extends React.Component<
  IBieOnboardingWorkbenchProps,
  IBieOnboardingWorkbenchComponentState
> {
  //private readonly api: BieOnboardingWorkbenchApi;

  private mounted = false;

  private workbenchRequestVersion = 0;
  private issueRequestVersion = 0;
  private ruleRequestVersion = 0;

  public constructor(props: IBieOnboardingWorkbenchProps) {
    super(props);

    //this.api = props.api; // || bieOnboardingWorkbenchApi;

    this.state = {
      ...emptyBieOnboardingWorkbenchState,

      issuesLoading: false,
      rulesLoading: false,
      publishing: false,
      publicationResult: null,

      validationDialogOpen: false,

      selectedImportId: '',
      selectedIssueId: '',
      selectedRuleId: '',

      loadSessionId: this.resolveLoadSessionId(props),

      stagingReview: null,
      loadingStagingReview: false,
      refreshingStagingReview: false,
      stagingErrorMessage: '',

      staging: false,
      reopening: false,

      decisionDialogOpen: false,
      decisionIssue: null,
      decisionCode: 'KEEP',
      decisionReason: '',
      decisionLoading: false,
      decisionErrorMessage: '',
      selectedDuplicateItemIds: [],

      thresholdDialogOpen: false,
      thresholdIssue: null,
      thresholdReason: '',
      thresholdEvidence: '',
      thresholdLoading: false,
      thresholdErrorMessage: '',

      governanceDecisions: [],

      resetDialogOpen: false,
      resettingStaging: false,

      selectedGovernanceDecisionId: '',
      governanceStateFilter: 'ALL',

      governanceReviewDialogOpen: false,
      governanceReviewDecision: null,
      governanceReviewAction: '',
      governanceReviewNotes: '',
      governanceReviewLoading: false,
      governanceReviewErrorMessage: '',
    };
  }

  public componentDidMount(): void {
    this.mounted = true;

    if (this.props.autoLoad !== false) {
      void this.loadWorkbench(true);
    }
  }

  public componentDidUpdate(previousProps: IBieOnboardingWorkbenchProps): void {
    if (
      String(this.resolveLoadSessionId(previousProps)) !==
      String(this.resolveLoadSessionId(this.props))
    ) {
      this.resetForLoadSessionChange(this.resolveLoadSessionId(this.props));

      void this.loadWorkbench(true);
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;

    /*
     * Invalidate outstanding requests so their responses are
     * ignored after unmounting.
     */
    this.workbenchRequestVersion += 1;
    this.issueRequestVersion += 1;
    this.ruleRequestVersion += 1;
  }

  public render(): React.ReactNode {
    const { workbench, loading, errorMessage } = this.state;

    return (
      <FWorkbenchShell
        //title={this.props.title || 'Institutional Onboarding Workbench'}
        title={'Institutional Onboarding Workbench'}
        subtitle={
          workbench
            ? `${workbench.loadSession.code} · ${workbench.loadSession.name}`
            : 'Review staged imports, validation issues and publication readiness.'
        }
        icon={<FactCheckOutlinedIcon />}
        loading={loading && !workbench}
        error={errorMessage}
        onBack={this.handleBack}
        onRefresh={this.handleRefreshWorkbench}
        actions={this.renderPrimaryWorkflowAction()}
      >
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* {this.renderPrimaryWorkflowAction()} */}
          {this.renderPageHeader()}
          {this.renderTabs()}
          {this.renderSelectedTab()}
          {this.renderValidationDialog()}
          {this.renderNotifications()}
        </Stack>
        <BieImportDecisionDialog
          open={this.state.decisionDialogOpen}
          issue={this.state.decisionIssue}
          decisionCode={this.state.decisionCode}
          reason={this.state.decisionReason}
          loading={this.state.decisionLoading}
          errorMessage={this.state.decisionErrorMessage}
          onDecisionCodeChange={this.handleDecisionCodeChange}
          onReasonChange={this.handleDecisionReasonChange}
          onClose={this.handleCloseDecisionDialog}
          onConfirm={this.handleConfirmDecision}
        />
        <BieThresholdDecisionDialog
          open={this.state.thresholdDialogOpen}
          issue={this.state.thresholdIssue}
          reason={this.state.thresholdReason}
          evidence={this.state.thresholdEvidence}
          loading={this.state.thresholdLoading}
          errorMessage={this.state.thresholdErrorMessage}
          onReasonChange={(thresholdReason) => this.setState({ thresholdReason, thresholdErrorMessage: '' })}
          onEvidenceChange={(thresholdEvidence) => this.setState({ thresholdEvidence, thresholdErrorMessage: '' })}
          onOpenDriver={this.handleOpenThresholdDriver}
          onOpenRecord={this.handleOpenThresholdRecord}
          onClose={this.handleCloseThresholdDialog}
          onSubmit={this.handleSubmitThresholdRequest}
        />
        {this.renderResetDialog()}
        {this.renderGovernanceReviewDialog()}
      </FWorkbenchShell>
    );
  }

  private resolveLoadSessionId(
    props: IBieOnboardingWorkbenchProps = this.props
  ): BieRecordId {
    return props.loadSessionId ?? props.params?.loadsessionid ?? '';
  }

  private readonly handleBack = (): void => {
    if (this.props.onBack) {
      this.props.onBack();
      return;
    }

    const origin = String(
      this.props.params?.workbenchorigin || PAGE_LOAD_SESSION_MANAGER
    );

    this.props.setPage(
      origin === PAGE_LOAD_SESSION_WIZARD
        ? PAGE_LOAD_SESSION_WIZARD
        : PAGE_LOAD_SESSION_MANAGER
    );
  };



  /* ==========================================================
   * Page header
   * ==========================================================
   */

  private renderPageHeader(): React.ReactNode {
    const { workbench, loading, refreshing, validating, publishing } =
      this.state;

    const busy = loading || refreshing || validating || publishing;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              minWidth: 0,
            }}
          >
            {this.props.onBack && (
              <Tooltip title="Back" arrow>
                <IconButton onClick={this.props.onBack} disabled={busy}>
                  <ArrowBackOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography variant="h5" fontWeight={700} noWrap>
                {/* {this.props.title || 'Institutional Onboarding Workbench'} */}
                {'Onboarding Review'}
              </Typography>

              <Typography variant="body2" color="text.secondary" noWrap>
                {workbench
                  ? `${workbench.loadSession.code} · ${
                      workbench.loadSession.name
                    }`
                  : 'Loading the selected Load Session'}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{
              xs: 'space-between',
              sm: 'flex-end',
            }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            {workbench && (
              <>
                <BieOnboardingStatusChip
                  kind="LOAD_SESSION"
                  value={workbench.loadSession.stateCode}
                />

                <BieOnboardingStatusChip
                  kind="VALIDATION"
                  value={workbench.summary.validationStatus}
                />
              </>
            )}

            {
              //String(workbench?.loadSession.stateCode).toUpperCase() === 'REJECTED' && (
              this.canReopenForCorrection() && (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={this.state.reopening}
                  startIcon={<ReplayOutlinedIcon />}
                  onClick={() => void this.handleReopenForCorrection()}
                >
                  Reopen for Correction
                </Button>
              )
            }

            {this.canResetStaging() && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<DeleteSweepOutlinedIcon />}
                disabled={busy || this.state.resettingStaging}
                onClick={() =>
                  this.setState({ resetDialogOpen: true })
                }
              >
                Reset Staging
              </Button>
            )}

            <Tooltip title="Refresh Workbench" arrow>
              <span>
                <IconButton
                  disabled={busy}
                  onClick={this.handleRefreshWorkbench}
                >
                  {refreshing ? (
                    <CircularProgress size={22} />
                  ) : (
                    <RefreshOutlinedIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Primary Workflow Action
   * ==========================================================
   */

  private renderPrimaryWorkflowAction():React.ReactNode {
    const workbench = this.state.workbench;
    //console.log('workbench:',workbench);

    if (!workbench) {
      return null;
    }

    //console.log('workbench.valid:', JSON.stringify(workbench));

    if (workbench.actions.canStage) {
      return (
        <Button
          variant="contained"
          startIcon={
            this.state.staging
              ? <CircularProgress size={18} />
              : <InventoryIcon />
          }
          disabled={
            this.state.staging ||
            this.state.validating ||
            this.state.loading
          }
          onClick={this.handleCompleteStaging}
        >
          {this.state.staging
            ? 'Completing Staging...'
            : 'Complete Staging'}
        </Button>
      );
    }

    if (this.hasAdministrativeResolutionPending()) {
      return (
        <Button
          variant="contained"
          startIcon={<AutorenewOutlinedIcon />}
          onClick={this.handleResolveAndRevalidate}
          disabled={this.state.validating}
        >
          Resolve and Revalidate
        </Button>
      );
    }

    if (workbench.actions.canValidate || workbench.actions.canRevalidate) {
      return (
        <Button
          variant="contained"
          startIcon={
            this.state.validating
              ? <CircularProgress size={18} />
              : <FactCheckIcon />
          }
          disabled={
            this.state.validating ||
            this.state.staging ||
            this.state.loading
          }
          onClick={this.handleOpenValidationDialog}
        >
          {this.state.validating
            ? 'Validating...'
            : workbench.actions.canRevalidate
              ? 'Revalidate Workbook'
              : 'Validate Workbook'}
        </Button>
      );
    }

    if (workbench.actions.canPublish) {
      return (
        <Button
          variant="contained"
          startIcon={
            this.state.publishing
              ? <CircularProgress size={18} />
              : <PublishOutlinedIcon />
          }
          disabled={this.state.publishing || this.state.loading || this.state.refreshing}
          onClick={this.handlePublish}
        >
          {this.state.publishing ? 'Publishing...' : 'Publish'}
        </Button>
      );
    }

    if (String(workbench.loadSession.stateCode).toUpperCase() === 'PUBLISHED') {
      return (
        <Button
          variant="contained"
          startIcon={<ExitToAppOutlinedIcon />}
          disabled={this.state.loading || this.state.refreshing}
          onClick={this.handleExitToPlanningCycle}
        >
          Exit to Planning Cycle
        </Button>
      );
    }

    return null;
  }

  /* ==========================================================
   * Tabs
   * ==========================================================
   */

  private renderTabs(): React.ReactNode {
    const workbench = this.state.workbench;

    const governanceCount =
      this.state.governanceDecisions?.length || 0;

    // const governancePendingCount =
    //   this.state.governanceDecisions?.filter(
    //     decision =>
    //       decision.ssc === 'REQUESTED'
    //       || decision.ssc === 'UNDER_REVIEW',
    //   ).length || 0;

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={this.state.selectedTab}
          onChange={this.handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,

            '& .MuiTab-root': {
              minHeight: 56,
              fontWeight: 600,
            },
          }}
        >
          <Tab
            value="OVERVIEW"
            icon={<AssessmentOutlinedIcon />}
            iconPosition="start"
            label="Overview"
          />

          <Tab
            value="STAGING"
            icon={<AssessmentOutlinedIcon />}
            iconPosition="start"
            label="Staging & Resolution"
          />

          <Tab
            value="IMPORTS"
            icon={<DatasetOutlinedIcon />}
            iconPosition="start"
            label={
              workbench
                ? `Imports (${this.formatNumber(workbench.imports.length)})`
                : 'Imports'
            }
          />

          <Tab
            value="ISSUES"
            icon={<FactCheckOutlinedIcon />}
            iconPosition="start"
            label={
              workbench
                ? `Issues (${this.formatNumber(
                    workbench.summary.issues.total
                  )})`
                : 'Issues'
            }
          />

          <Tab
            value="GOVERNANCE"
            icon={<GavelOutlinedIcon />}
            iconPosition="start"
            disabled={governanceCount === 0}
            label={
              governanceCount > 0
                ? `Governance (${governanceCount})`
                : 'Governance'
            }
          />

          <Tab
            value="RULES"
            icon={<RuleOutlinedIcon />}
            iconPosition="start"
            label="Validation Rules"
          />

          <Tab
            value="PUBLISH"
            icon={<PublishOutlinedIcon />}
            iconPosition="start"
            label="Publish"
            disabled={!workbench || (!workbench.actions.canPublish && String(workbench.loadSession.stateCode).toUpperCase() !== 'PUBLISHED')}
          />
        </Tabs>
      </Paper>
    );
  }

  /* ==========================================================
   * Selected tab
   * ==========================================================
   */

  private renderSelectedTab(): React.ReactNode {
    switch (this.state.selectedTab) {
      case 'IMPORTS':
        return this.renderImportsTab();

      case 'ISSUES':
        return this.renderIssuesTab();

      case 'GOVERNANCE':
        return this.renderGovernanceTab();

      case 'RULES':
        return this.renderRulesTab();

      case 'STAGING':
        return this.renderStagingTab();

      case 'PUBLISH':
        return this.renderPublishTab();

      case 'OVERVIEW':
      default:
        return this.renderOverviewTab();
    }
  }

  /* ==========================================================
   * Overview tab
   * ==========================================================
   */

  private renderOverviewTab(): React.ReactNode {
    return (
      <BieOnboardingOverview
        workbench={this.state.workbench}
        loading={this.state.loading}
        refreshing={this.state.refreshing}
        validating={this.state.validating}
        publishing={this.state.publishing}
        errorMessage={this.state.errorMessage}
        onRefresh={this.handleRefreshWorkbench}
        onValidate={this.handleOpenValidationDialog}
        onPublish={undefined}
        onViewAllIssues={this.handleViewAllIssues}
        onViewImportIssues={this.handleViewImportIssues}
        onViewImportRecords={this.handleViewImportRecords}
        onOpenIssueRecord={this.handleOpenIssueRecord}
      />
    );
  }

  /* ==========================================================
   * Publish tab
   * ==========================================================
   */

  private renderPublishTab(): React.ReactNode {
    return (
      <BieOnboardingPublish
        workbench={this.state.workbench}
        publishing={this.state.publishing}
        result={this.state.publicationResult}
      />
    );
  }

  /* ==========================================================
   * Staging tab
   * ==========================================================
   */

  // private renderStagingTab(): React.ReactNode {
  //   return (
  //     //{
  //     // this.state.activeTab === 'staging' && (
  //     <BieOnboardingWorkbenchStaging
  //       review={this.state.stagingReview}
  //       loading={this.state.loadingStagingReview}
  //       refreshing={this.state.refreshingStagingReview}
  //       disabled={this.state.staging || this.state.validating}
  //       onRefresh={this.handleRefreshStagingReview}
  //       onOpenImportRecords={this.handleOpenImportRecords}
  //     />
  //     //)
  //     //}
  //   );
  // }

  private renderStagingTab(): React.ReactNode {
    return (
      <Stack spacing={2}>
        {this.state.stagingErrorMessage && (
          <Alert
            severity="error"
            onClose={() =>
              this.setState({
                stagingErrorMessage: '',
              })
            }
          >
            {this.state.stagingErrorMessage}
          </Alert>
        )}

        <BieOnboardingWorkbenchStaging
          review={this.state.stagingReview}
          loading={this.state.loadingStagingReview}
          refreshing={this.state.refreshingStagingReview}
          disabled={this.state.loading || this.state.validating}
          onRefresh={this.handleRefreshStagingReview}
          onOpenImportRecords={this.handleOpenStagedImportRecords}
        />
      </Stack>
    );
  }

  /* ==========================================================
   * Imports tab
   * ==========================================================
   */

  private renderImportsTab(): React.ReactNode {
    const { workbench, loading, refreshing, selectedImportId } = this.state;

    if (loading && !workbench) {
      return this.renderTabLoading('Loading workbook imports…');
    }

    if (!workbench) {
      return this.renderTabUnavailable();
    }

    return (
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              sm: 'center',
            }}
            spacing={1}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Workbook Imports
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Review staged records and validation outcomes for each uploaded
                worksheet.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <BieOnboardingStatusChip
                kind="READINESS"
                value={workbench.summary.validationReady}
              />

              <Tooltip title="Refresh Imports" arrow>
                <span>
                  <IconButton
                    disabled={refreshing || this.state.validating}
                    onClick={this.handleRefreshWorkbench}
                  >
                    <RefreshOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {workbench.imports.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              py: 7,
              px: 3,
              textAlign: 'center',
            }}
          >
            <DatasetOutlinedIcon
              color="disabled"
              sx={{
                fontSize: 52,
              }}
            />

            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                mt: 1,
              }}
            >
              No workbook imports found
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
              }}
            >
              The selected Load Session does not contain active staged worksheet
              imports.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {workbench.imports.map((item) => (
              <Grid size={{ xs: 12, md: 6, xl: 4 }} key={String(item.rid)}>
                <BieOnboardingImportCard
                  data={item}
                  selected={String(selectedImportId) === String(item.rid)}
                  disabled={this.state.validating || this.state.publishing}
                  onClick={this.handleSelectImport}
                  onViewIssues={this.handleViewImportIssues}
                  onViewRecords={this.handleViewImportRecords}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    );
  }

  /* ==========================================================
   * Issues tab
   * ==========================================================
   */

  private renderIssuesTab(): React.ReactNode {
    const workbench = this.state.workbench;

    return (
      <BieOnboardingIssueGrid
        issues={this.state.issues}
        imports={workbench?.imports || []}
        severities={workbench?.lookups.severities || []}
        rules={workbench?.lookups.rules || []}
        filters={this.state.filters}
        loading={this.state.issuesLoading && this.state.issues.length === 0}
        refreshing={this.state.issuesLoading && this.state.issues.length > 0}
        errorMessage={
          this.state.selectedTab === 'ISSUES' ? this.state.errorMessage : ''
        }
        selectedIssueId={this.state.selectedIssueId}
        pageOffset={this.state.issuePageOffset}
        pageLimit={this.state.issuePageLimit}
        hasNextPage={this.state.issues.length >= this.state.issuePageLimit}
        disabled={this.state.validating}
        onRemediation={this.handleIssueRemediation}
        onFiltersChange={this.handleIssueFiltersChange}
        onApplyFilters={this.handleApplyIssueFilters}
        onClearFilters={this.handleClearIssueFilters}
        onRefresh={this.handleRefreshIssues}
        onPageChange={this.handleIssuePageChange}
        onPageLimitChange={this.handleIssuePageLimitChange}
        onSelectIssue={this.handleSelectIssue}
        onOpenRecord={this.handleOpenIssueRecord}
        selectedDuplicateItemIds={this.state.selectedDuplicateItemIds}
        decisionLoading={this.state.decisionLoading}
        onToggleDuplicateItem={this.handleToggleDuplicateItem}
        onKeepExactDuplicates={this.handleKeepExactDuplicates}
        onBulkDuplicateDecision={this.handleBulkDuplicateDecision}
        onResolveDuplicate={this.handleOpenDecisionDialog}
        onResolveThreshold={this.handleOpenThresholdDialog}
      />
    );
  }

  /* ==========================================================
   * Rules tab
   * ==========================================================
   */

  private renderRulesTab(): React.ReactNode {
    return (
      <BieOnboardingRuleGrid
        rules={this.state.rules}
        issueSummaries={this.state.workbench?.summary.byRule || []}
        severities={this.state.workbench?.lookups.severities || []}
        loading={this.state.rulesLoading && this.state.rules.length === 0}
        refreshing={this.state.rulesLoading && this.state.rules.length > 0}
        errorMessage={
          this.state.selectedTab === 'RULES' ? this.state.errorMessage : ''
        }
        selectedRuleId={this.state.selectedRuleId}
        onRefresh={this.handleRefreshRules}
        onSelectRule={this.handleSelectRule}
        onViewRuleIssues={this.handleViewRuleIssues}
      />
    );
  }

  /* ==========================================================
   * Governance tab
   * ==========================================================
   */
  // private renderGovernanceTab0(): React.ReactNode {
  //   return (
  //     <BieGovernanceDecisionGrid
  //       rows={this.state.governanceDecisions}
  //       currentUserId={this.props.userData?.rid}
  //       loading={
  //         this.state.loading
  //         || this.state.validating
  //         || this.state.thresholdLoading
  //       }

  //       // Replace with the established framework permission if one is already
  //       // available in PageProps/actionSet. DB maker/checker remains authoritative.
  //       canReview={true}

  //       onReview={this.handleReviewGovernanceDecision}
  //       onWithdraw={this.handleWithdrawGovernanceDecision}
  //       onOpenDriver={this.handleOpenGovernanceDriver}
  //     />
  //   );
  // }

  private renderGovernanceTab(): React.ReactNode {
    const decisions =
      this.state.governanceDecisions;

    const pendingCount =
      decisions.filter(
        decision =>
          decision.ssc === 'REQUESTED'
          || decision.ssc === 'UNDER_REVIEW'
      ).length;

    const approvedCount =
      decisions.filter(
        decision => decision.ssc === 'APPROVED'
      ).length;

    const rejectedCount =
      decisions.filter(
        decision => decision.ssc === 'REJECTED'
      ).length;

    return (
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              md: 'center',
            }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={800}
              >
                Governance Review
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Maker/checker review of governed onboarding
                exceptions and their audit trail.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                label={`Pending ${pendingCount}`}
                color={
                  pendingCount > 0
                    ? 'warning'
                    : 'default'
                }
                variant="outlined"
              />

              <Chip
                size="small"
                label={`Approved ${approvedCount}`}
                color="success"
                variant="outlined"
              />

              <Chip
                size="small"
                label={`Rejected ${rejectedCount}`}
                color="error"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Paper>

        {this.renderGovernanceToolbar()}

        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              lg: 7,
            }}
          >
            {this.renderGovernanceGrid()}
          </Grid>

          <Grid
            size={{
              xs: 12,
              lg: 5,
            }}
          >
            {this.renderGovernanceDetails()}
          </Grid>
        </Grid>
      </Stack>
    );
  }

  private renderGovernanceToolbar(): React.ReactNode {
    const filters = [
      ['ALL', 'All'],
      ['PENDING', 'Pending'],
      ['APPROVED', 'Approved'],
      ['REJECTED', 'Rejected'],
      ['WITHDRAWN', 'Withdrawn'],
    ];

    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            md: 'center',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            {filters.map(
              ([code, name]) => (
                <Chip
                  key={code}
                  size="small"
                  clickable
                  label={name}
                  color={
                    this.state.governanceStateFilter === code
                      ? 'primary'
                      : 'default'
                  }
                  variant={
                    this.state.governanceStateFilter === code
                      ? 'filled'
                      : 'outlined'
                  }
                  onClick={() =>
                    this.setState({
                      governanceStateFilter: code,
                      selectedGovernanceDecisionId: '',
                    })
                  }
                />
              )
            )}
          </Stack>

          <Button
            size="small"
            startIcon={<RefreshOutlinedIcon />}
            disabled={
              this.state.loading
              || this.state.refreshing
              || this.state.governanceReviewLoading
            }
            onClick={() =>
              void this.loadGovernanceDecisions()
            }
          >
            Refresh
          </Button>
        </Stack>
      </Paper>
    );
  }

  private renderGovernanceGrid(): React.ReactNode {
    const rows =
      this.getFilteredGovernanceDecisions();

    if (rows.length === 0) {
      return (
        <Paper
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
          >
            No Governance Decisions match the selected
            filter.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              '110px minmax(130px, 1fr) minmax(150px, 1.4fr) 120px 110px',
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={800}
          >
            Status
          </Typography>

          <Typography
            variant="caption"
            fontWeight={800}
          >
            Driver
          </Typography>

          <Typography
            variant="caption"
            fontWeight={800}
          >
            Rule
          </Typography>

          <Typography
            variant="caption"
            fontWeight={800}
            textAlign="right"
          >
            Value
          </Typography>

          <Typography
            variant="caption"
            fontWeight={800}
            textAlign="right"
          >
            Maximum
          </Typography>
        </Box>

        <Stack
          divider={<Divider flexItem />}
          spacing={0}
        >
          {rows.map(decision => {
            const selected =
              String(
                this.state.selectedGovernanceDecisionId
              ) === String(decision.rid);

            return (
              <Box
                key={String(decision.rid)}
                onClick={() =>
                  this.setState({
                    selectedGovernanceDecisionId:
                      decision.rid || '',
                  })
                }
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    '110px minmax(130px, 1fr) minmax(150px, 1.4fr) 120px 110px',
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  cursor: 'pointer',
                  bgcolor:
                    selected
                      ? 'action.selected'
                      : 'background.paper',

                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box>
                  <Chip
                    size="small"
                    label={
                      decision.ssn
                      || decision.ssc
                      || 'Unknown'
                    }
                    color={
                      this.getGovernanceStatusColor(
                        decision.ssc
                      )
                    }
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    noWrap
                  >
                    {decision.drn
                      || decision.drc
                      || '—'}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {decision.drc || ''}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                  >
                    {decision.run
                      || decision.ruc
                      || '—'}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {decision.ruc || ''}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  textAlign="right"
                  fontWeight={700}
                >
                  {this.formatGovernanceAmount(
                    decision.vlr
                  )}
                </Typography>

                <Typography
                  variant="body2"
                  textAlign="right"
                >
                  {this.formatGovernanceAmount(
                    decision.mxv
                  )}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Paper>
    );
  }

  private renderGovernanceDetails(): React.ReactNode {
    const decision =
      this.getSelectedGovernanceDecision();

    if (!decision) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            minHeight: 260,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={800}
            gutterBottom
          >
            Decision Details
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            Select a Governance Decision to review its
            justification, values, evidence and available
            actions.
          </Typography>
        </Paper>
      );
    }

    const currentUserId =
      this.getCurrentUserId();

    const requester =
      String(decision.rby || '')
      === String(currentUserId || '');

    const stateCode =
      String(decision.ssc || '')
        .toUpperCase();

    const openDecision =
      stateCode === 'REQUESTED'
      || stateCode === 'UNDER_REVIEW';

    const excess =
      decision.vlr !== null
        && decision.vlr !== undefined
        && decision.mxv !== null
        && decision.mxv !== undefined
        ? Number(decision.vlr)
        - Number(decision.mxv)
        : null;

    return (
      <Paper
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={800}
              >
                Decision Details
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {decision.ruc
                  || 'Governed Exception'}
              </Typography>
            </Box>

            <Chip
              size="small"
              label={
                decision.ssn
                || decision.ssc
                || 'Unknown'
              }
              color={
                this.getGovernanceStatusColor(
                  decision.ssc
                )
              }
            />
          </Stack>

          <Divider />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Incoming Value
              </Typography>

              <Typography fontWeight={800}>
                {this.formatGovernanceAmount(
                  decision.vlr
                )}
              </Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Maximum
              </Typography>

              <Typography fontWeight={800}>
                {this.formatGovernanceAmount(
                  decision.mxv
                )}
              </Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Excess
              </Typography>

              <Typography fontWeight={800}>
                {this.formatGovernanceAmount(
                  excess
                )}
              </Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Fiscal Year
              </Typography>

              <Typography>
                {decision.fsy || '—'}
              </Typography>
            </Grid>
          </Grid>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Driver
            </Typography>

            <Typography variant="body2">
              {decision.drn
                || decision.drc
                || '—'}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Request Justification
            </Typography>

            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {decision.rqr || '—'}
            </Typography>
          </Box>

          {decision.rqe && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Supporting Evidence
              </Typography>

              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {decision.rqe}
              </Typography>
            </Box>
          )}

          {decision.rvn && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
              >
                Review Notes
              </Typography>

              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {decision.rvn}
              </Typography>
            </Box>
          )}

          <Divider />

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon />}
              onClick={() =>
                this.handleOpenGovernanceDriver(
                  decision
                )
              }
            >
              Driver
            </Button>

            {decision.iti && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewOutlinedIcon />}
                onClick={() => this.handleOpenGovernanceImportItem(decision)}
              >
                Import Item
              </Button>
            )}

            {requester && openDecision && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<UndoOutlinedIcon />}
                onClick={() =>
                  this.handleOpenGovernanceReviewDialog(
                    decision,
                    'WITHDRAW'
                  )
                }
              >
                Withdraw
              </Button>
            )}

            {!requester
              && stateCode === 'REQUESTED'
              && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    this.handleOpenGovernanceReviewDialog(
                      decision,
                      'REVIEW'
                    )
                  }
                >
                  Take Review
                </Button>
              )}

            {!requester
              && openDecision
              && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() =>
                      this.handleOpenGovernanceReviewDialog(
                        decision,
                        'APPROVE'
                      )
                    }
                  >
                    Approve
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      this.handleOpenGovernanceReviewDialog(
                        decision,
                        'REJECT'
                      )
                    }
                  >
                    Reject
                  </Button>
                </>
              )}
          </Stack>

          {requester && openDecision && (
            <Alert severity="info">
              This request requires an independent
              reviewer. The requester cannot approve or
              reject their own Governance Decision.
            </Alert>
          )}
        </Stack>
      </Paper>
    );
  }

  private readonly handleOpenGovernanceReviewDialog = (
    decision: IBieGovernanceDecision,
    action: BieGovernanceUiAction,
  ): void => {

    this.setState({
      governanceReviewDialogOpen: true,
      governanceReviewDecision: decision,
      governanceReviewAction: action,
      governanceReviewNotes: '',
      governanceReviewErrorMessage: '',
    });
  };


  private readonly handleCloseGovernanceReviewDialog =
    (): void => {

      if (this.state.governanceReviewLoading) {
        return;
      }

      this.setState({
        governanceReviewDialogOpen: false,
        governanceReviewDecision: null,
        governanceReviewAction: '',
        governanceReviewNotes: '',
        governanceReviewErrorMessage: '',
      });
    };

  private readonly handleSubmitGovernanceReview =
    async (): Promise<void> => {

      const decision =
        this.state.governanceReviewDecision;

      const action =
        this.state.governanceReviewAction;

      const notes =
        this.state.governanceReviewNotes.trim();

      if (
        !decision?.rid
        || !decision.stp
        || !action
      ) {
        return;
      }

      if (
        ['APPROVE', 'REJECT', 'WITHDRAW']
          .includes(action)
        && !notes
      ) {
        this.setState({
          governanceReviewErrorMessage:
            'A reason or review note is required.',
        });

        return;
      }

      this.setState({
        governanceReviewLoading: true,
        governanceReviewErrorMessage: '',
      });

      try {
        if (action === 'WITHDRAW') {
          await withdrawBieGovernanceDecision(
            decision.rid,
            notes,
            decision.stp,
          );

          this.setState({
            governanceReviewDialogOpen: false,
            governanceReviewDecision: null,
            governanceReviewAction: '',
            governanceReviewNotes: '',
            governanceReviewLoading: false,
            selectedGovernanceDecisionId: '',
          });

          await this.loadWorkbench();

          this.props.showNotification?.(
            'Governance Decision withdrawn.',
            'success',
          );

          return;
        }

        const result =
          await reviewBieGovernanceDecision(
            decision.rid,
            action,
            notes,
            decision.stp,
          );

        this.setState({
          governanceReviewDialogOpen: false,
          governanceReviewDecision: null,
          governanceReviewAction: '',
          governanceReviewNotes: '',
          governanceReviewLoading: false,
        });

        const nextAction =
          String(
            result?.nextaction?.action || ''
          ).toUpperCase();

        if (nextAction === 'REVALIDATE') {
          await this.revalidateAfterGovernanceApproval(
            'Governance Decision approved. Validation has been refreshed.'
          );
        } else {
          await this.loadWorkbench();

          this.props.showNotification?.(
            action === 'REVIEW'
              ? 'Governance Decision is now under review.'
              : 'Governance Decision rejected.',
            action === 'REJECT'
              ? 'warning'
              : 'success',
          );
        }
      } catch (error) {
        this.setState({
          governanceReviewLoading: false,
          governanceReviewErrorMessage:
            this.getErrorMessage(error),
        });
      }
    };

  private readonly handleOpenGovernanceDriver = (
    decision: IBieGovernanceDecision,
  ): void => {

    if (!decision.dri) {
      return;
    }

    this.props.onParam('set')(
      'rid',
      decision.dri
    );

    this.props.onParam('set')(
      'returnpage',
      'bieonboardingworkbench'
    );

    this.props.setPage(
      PAGE_DRIVER_THRESHOLD
    );
  };

  //
  private readonly handleOpenGovernanceImportItem = (
    decision: IBieGovernanceDecision,
  ): void => {

    if (!decision.iti) {
      return;
    }

    this.props.onParam('set')('rid', decision.iti);
    this.props.setPage('bieimportitem');
  };

  private renderGovernanceReviewDialog(): React.ReactNode {
    const action =
      this.state.governanceReviewAction;

    const decision =
      this.state.governanceReviewDecision;

    const title =
      action === 'APPROVE'
        ? 'Approve Governance Decision'
        : action === 'REJECT'
          ? 'Reject Governance Decision'
          : action === 'WITHDRAW'
            ? 'Withdraw Governance Decision'
            : 'Take Governance Decision for Review';

    const fieldLabel =
      action === 'APPROVE'
        ? 'Approval Notes'
        : action === 'REJECT'
          ? 'Rejection Reason'
          : action === 'WITHDRAW'
            ? 'Withdrawal Reason'
            : 'Review Notes';

    return (
      <Dialog
        open={
          this.state.governanceReviewDialogOpen
        }
        onClose={
          this.handleCloseGovernanceReviewDialog
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {title}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {this.state.governanceReviewErrorMessage && (
              <Alert severity="error">
                {
                  this.state
                    .governanceReviewErrorMessage
                }
              </Alert>
            )}

            {decision && (
              <Alert severity="info">
                {decision.drc || decision.drn || 'Driver'}
                {' · '}
                {this.formatGovernanceAmount(
                  decision.vlr
                )}
                {' against maximum '}
                {this.formatGovernanceAmount(
                  decision.mxv
                )}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label={fieldLabel}
              value={
                this.state.governanceReviewNotes
              }
              disabled={
                this.state.governanceReviewLoading
              }
              onChange={event =>
                this.setState({
                  governanceReviewNotes:
                    event.target.value,
                  governanceReviewErrorMessage: '',
                })
              }
              required={
                action === 'APPROVE'
                || action === 'REJECT'
                || action === 'WITHDRAW'
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={
              this.state.governanceReviewLoading
            }
            onClick={
              this.handleCloseGovernanceReviewDialog
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color={
              action === 'REJECT'
                ? 'error'
                : action === 'WITHDRAW'
                  ? 'warning'
                  : 'primary'
            }
            disabled={
              this.state.governanceReviewLoading
            }
            onClick={() =>
              void this.handleSubmitGovernanceReview()
            }
          >
            {action === 'REVIEW'
              ? 'Take Review'
              : action === 'WITHDRAW'
                ? 'Withdraw'
                : action === 'REJECT'
                  ? 'Reject'
                  : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  private renderResetDialog(): React.ReactNode {
    return (
      <Dialog
        open={this.state.resetDialogOpen}
        onClose={() => {
          if (!this.state.resettingStaging) {
            this.setState({
              resetDialogOpen: false,
            });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reset Onboarding Staging?
        </DialogTitle>

        <DialogContent dividers>
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
          >
            This permanently removes the current
            unpublished staging generation.
            Administrative configuration such as
            Unit Aliases and Driver settings will
            not be removed.
          </Alert>

          <Typography variant="body2">
            The following will be removed:
          </Typography>

          <Box component="ul" sx={{ mb: 0 }}>
            <li>Workbook imports and staged records</li>
            <li>Validation issues</li>
            <li>Existing Canonical Fact decisions</li>
            <li>Governance Decision requests and reviews</li>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={this.state.resettingStaging}
            onClick={() =>
              this.setState({
                resetDialogOpen: false,
              })
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="warning"
            disabled={this.state.resettingStaging}
            onClick={() =>
              void this.handleResetStaging()
            }
          >
            Reset Staging
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* ==========================================================
   * Workbench loading
   * ==========================================================
   */

  private async loadWorkbench(initialLoad = false): Promise<void> {
    const requestVersion = ++this.workbenchRequestVersion;

    this.safeSetState({
      loading: initialLoad,
      refreshing: !initialLoad,
      errorMessage: '',
    });

    try {
      //const workbench = await this.api.getWorkbench(this.props.loadSessionId);
      const loadSessionId = this.resolveLoadSessionId();

      if (!loadSessionId || Number(loadSessionId) <= 0) {
        throw new Error(
          'The onboarding Workbench requires a valid Load Session ID.'
        );
      }

      if (String(this.state.loadSessionId) !== String(loadSessionId)) {
        this.safeSetState({ loadSessionId });
      }

      const workbench = await fetchBieOnboardingWorkbench(loadSessionId);

      if (!this.mounted || requestVersion !== this.workbenchRequestVersion) {
        return;
      }

      this.setState(
        {
          workbench,

          loading: false,
          refreshing: false,

          errorMessage: '',

          /*
           * Keep the issue list synchronized when the validation
           * result contains no current issues.
           */
          issues: workbench.summary.issues.total > 0 ? this.state.issues : [],

          selectedIssueId:
            workbench.summary.issues.total > 0
              ? this.state.selectedIssueId
              : '',
        },
        () => {
          this.props.onWorkbenchChange?.(workbench);
        }
      );

      await this.loadGovernanceDecisions(loadSessionId);

    } catch (error) {
      if (!this.mounted || requestVersion !== this.workbenchRequestVersion) {
        return;
      }

      this.setState({
        loading: false,
        refreshing: false,

        errorMessage: this.getErrorMessage(error),
      });
    }
  }


  private async loadWorkbench11111(initialLoad = false): Promise<void> {
    const requestVersion = ++this.workbenchRequestVersion;

    this.safeSetState({
      loading: initialLoad,
      refreshing: !initialLoad,
      errorMessage: '',
    });

    try {
      const loadSessionId = this.resolveLoadSessionId();

      if (!loadSessionId || Number(loadSessionId) <= 0) {
        throw new Error(
          'The onboarding Workbench requires a valid Load Session ID.'
        );
      }

      if (String(this.state.loadSessionId) !== String(loadSessionId)) {
        this.safeSetState({ loadSessionId });
      }

      const workbench =
        await fetchBieOnboardingWorkbench(loadSessionId);

      if (
        !this.mounted
        || requestVersion !== this.workbenchRequestVersion
      ) {
        return;
      }

      this.setState(
        {
          workbench,

          loading: false,
          refreshing: false,

          errorMessage: '',

          issues:
            workbench.summary.issues.total > 0
              ? this.state.issues
              : [],

          selectedIssueId:
            workbench.summary.issues.total > 0
              ? this.state.selectedIssueId
              : '',
        },
        () => {
          this.props.onWorkbenchChange?.(workbench);
        }
      );

      await this.loadGovernanceDecisions();

    } catch (error) {
      if (
        !this.mounted
        || requestVersion !== this.workbenchRequestVersion
      ) {
        return;
      }

      this.setState({
        loading: false,
        refreshing: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  /* ==========================================================
   * Staging loading
   * ==========================================================
   */
  private async loadStagingReview(
    refreshing = false,
  ): Promise<void> {
    const loadSessionId =
      this.resolveLoadSessionId();

    if (!loadSessionId) {
      this.setState({
        loadingStagingReview: false,
        refreshingStagingReview: false,
        stagingErrorMessage:
          'A Load Session is required to load staging results.',
      });

      return;
    }

    this.setState({
      loadingStagingReview: !refreshing,
      refreshingStagingReview: refreshing,
      stagingErrorMessage: '',
    });

    try {
      const review = await reviewBieOnboardingWorkbook(Number(loadSessionId));

      if (!this.mounted) {
        return;
      }

      this.setState({
        stagingReview: review as IBieOnboardingWorkbenchStagingData,
        loadingStagingReview: false,
        refreshingStagingReview: false,
      });

      if (review.stamp) {
        const setWorkbenchParam = this.props.onParam('set');
        setWorkbenchParam('stp',review.stamp);
      }
    } catch (error) {
      if (!this.mounted) {
        return;
      }

      this.setState({
        loadingStagingReview: false,
        refreshingStagingReview: false,
        stagingErrorMessage: this.getErrorMessage(error),
      });
    }
  }

  /* ==========================================================
   * Issue loading
   * ==========================================================
   */

  private async loadValidationIssues(): Promise<void> {
    const requestVersion = ++this.issueRequestVersion;

    this.safeSetState({
      issuesLoading: true,
      errorMessage: '',
    });

    try {
      const { filters, issuePageOffset, issuePageLimit } = this.state;

      //const issues = await this.api.findValidationIssues({
      const issues = await fetchBieOnboardingValidationIssues({
        loadSessionId: this.resolveLoadSessionId(),

        importId: filters.importId || null,

        severityCode: filters.severityCode || null,

        ruleCode: filters.ruleCode || null,

        classCode: filters.classCode || null,

        blocking: filters.blocking === '' ? null : Number(filters.blocking),

        search: filters.search || null,

        status: 1,

        pageOffset: issuePageOffset,

        pageLimit: issuePageLimit,
      });

      if (!this.mounted || requestVersion !== this.issueRequestVersion) {
        return;
      }

      this.setState({
        issues,
        issuesLoading: false,
        errorMessage: '',
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.issueRequestVersion) {
        return;
      }

      this.setState({
        issuesLoading: false,

        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  // ---------------------------------------------------------------------------
  // 1. LOAD GOVERNANCE DECISIONS
  // ---------------------------------------------------------------------------

  private readonly loadGovernanceDecisions0000 = async (): Promise<void> => {
    const loadSessionId = this.state.loadSessionId;

    if (!loadSessionId) {
      this.setState({ governanceDecisions: [] });
      return;
    }

    try {
      const governanceDecisions =
        await fetchBieGovernanceDecisions({
          loadSessionId,
          pageOffset: 0,
          pageLimit: 500,
        });

      if (!this.mounted) return;

      this.setState({ governanceDecisions });
    } catch (error) {
      if (!this.mounted) return;

      this.setState({
        governanceDecisions: [],
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly loadGovernanceDecisions = async (
    loadSessionId?: BieRecordId,
  ): Promise<void> => {
    const resolvedId =
      loadSessionId
      ?? this.state.loadSessionId
      ?? this.resolveLoadSessionId();

    if (!resolvedId) {
      this.safeSetState({
        governanceDecisions: [],
      });

      return;
    }

    const governanceDecisions =
      await fetchBieGovernanceDecisions({
        loadSessionId: resolvedId,
        pageOffset: 0,
        pageLimit: 500,
      });

    if (!this.mounted) {
      return;
    }

    this.safeSetState({
      governanceDecisions,
    });
  };

  /* ==========================================================
   * Duplicate decisions
   * ==========================================================
   */

  private readonly handleToggleDuplicateItem = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    const id = issue.iti;
    const exists = this.state.selectedDuplicateItemIds.some(
      (itemId) => String(itemId) === String(id),
    );

    this.setState({
      selectedDuplicateItemIds: exists
        ? this.state.selectedDuplicateItemIds.filter(
            (itemId) => String(itemId) !== String(id),
          )
        : [...this.state.selectedDuplicateItemIds, id],
    });
  };

  private readonly handleOpenDecisionDialog = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    this.setState({
      decisionDialogOpen: true,
      decisionIssue: issue,
      decisionCode: 'KEEP',
      decisionReason: '',
      decisionErrorMessage: '',
    });
  };

  private readonly handleCloseDecisionDialog = (): void => {
    if (this.state.decisionLoading) return;

    this.setState({
      decisionDialogOpen: false,
      decisionIssue: null,
      decisionReason: '',
      decisionErrorMessage: '',
    });
  };

  private readonly handleDecisionCodeChange = (
    decisionCode: BieImportDecisionCode,
  ): void => {
    this.setState({ decisionCode, decisionErrorMessage: '' });
  };

  private readonly handleDecisionReasonChange = (decisionReason: string): void => {
    this.setState({ decisionReason, decisionErrorMessage: '' });
  };

  private async revalidateAfterDecision00(successMessage: string): Promise<void> {
    const loadSessionId = this.resolveLoadSessionId();
    const stamp = this.state.workbench?.loadSession?.stamp ?? this.props.params?.stp;

    if (!loadSessionId || !stamp) {
      throw new Error('A valid Load Session and concurrency stamp are required for revalidation.');
    }

    const result = await validateBieOnboardingReview(loadSessionId, stamp);

    if (!this.mounted) return;

    this.props.onParam('set')('stp', result.validation.stamp);

    this.setState(
      {
        workbench: result.workbench,
        decisionDialogOpen: false,
        decisionIssue: null,
        decisionReason: '',
        decisionLoading: false,
        decisionErrorMessage: '',
        selectedDuplicateItemIds: [],
        successMessage,
        errorMessage: '',
        issuePageOffset: 0,
      },
      () => {
        this.props.onWorkbenchChange?.(result.workbench);
        void this.loadValidationIssues();
      },
    );

    this.props.showNotification?.(successMessage, 'success');
  }
  private async revalidateAfterDecision(
    successMessage: string,
  ): Promise<void> {
    await this.refreshValidationState();

    if (!this.mounted) {
      return;
    }

    this.setState({
      decisionDialogOpen: false,
      decisionIssue: null,
      decisionReason: '',
      decisionLoading: false,
      decisionErrorMessage: '',
      selectedDuplicateItemIds: [],
      successMessage,
    });

    this.props.showNotification?.(
      successMessage,
      'success',
    );
  }

  private readonly handleConfirmDecision = async (): Promise<void> => {
    const issue = this.state.decisionIssue;
    if (!issue) return;

    this.setState({ decisionLoading: true, decisionErrorMessage: '' });

    try {
      await setBieImportDecision(
        issue.iti,
        this.state.decisionCode,
        this.state.decisionReason.trim(),
      );

      await this.revalidateAfterDecision(
        `${this.state.decisionCode} decision applied and onboarding revalidated.`,
      );
    } catch (error) {
      if (!this.mounted) return;
      this.setState({
        decisionLoading: false,
        decisionErrorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly handleKeepExactDuplicates = async (
    importId: BieRecordId,
  ): Promise<void> => {
    this.setState({ decisionLoading: true, errorMessage: '' });

    try {
      await keepExactBieImportDuplicates(
        importId,
        'Exact duplicate retained from the existing canonical dataset.',
      );

      await this.revalidateAfterDecision(
        'Exact duplicate decisions applied and onboarding revalidated.',
      );
    } catch (error) {
      if (!this.mounted) return;
      this.setState({
        decisionLoading: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly handleBulkDuplicateDecision = async (
    decisionCode: BieImportDecisionCode,
  ): Promise<void> => {
    const selectedIds = this.state.selectedDuplicateItemIds;
    const selectedIssues = this.state.issues.filter((issue) =>
      selectedIds.some((id) => String(id) === String(issue.iti)),
    );
    const importIds = Array.from(
      new Set(selectedIssues.map((issue) => String(issue.imi))),
    );

    if (selectedIds.length === 0) return;

    if (importIds.length !== 1) {
      this.setState({
        errorMessage: 'Bulk duplicate decisions must be applied to records from one Import at a time.',
      });
      return;
    }

    const reason = window.prompt(
      `${decisionCode} ${selectedIds.length} selected duplicate record(s). Enter a decision reason:`,
      decisionCode === 'KEEP'
        ? 'Reviewed and retained the existing canonical facts.'
        : '',
    );

    if (reason === null) return;
    if (decisionCode !== 'KEEP' && !reason.trim()) {
      this.setState({ errorMessage: 'A decision reason is required.' });
      return;
    }

    this.setState({ decisionLoading: true, errorMessage: '' });

    try {
      await bulkSetBieImportDecisions(
        Number(importIds[0]),
        selectedIds,
        decisionCode,
        reason.trim(),
      );

      await this.revalidateAfterDecision(
        `${decisionCode} decisions applied to ${selectedIds.length} record(s) and onboarding revalidated.`,
      );
    } catch (error) {
      if (!this.mounted) return;
      this.setState({
        decisionLoading: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  /* ==========================================================
   * Threshold management
   * ==========================================================
   */
  private readonly handleOpenThresholdDialog = (issue: IBieOnboardingValidationIssue): void => {
    this.setState({
      thresholdDialogOpen: true,
      thresholdIssue: issue,
      thresholdReason: '',
      thresholdEvidence: '',
      thresholdErrorMessage: '',
    });
  };

  private readonly handleCloseThresholdDialog = (): void => {
    if (this.state.thresholdLoading) return;
    this.setState({
      thresholdDialogOpen: false,
      thresholdIssue: null,
      thresholdReason: '',
      thresholdEvidence: '',
      thresholdErrorMessage: '',
    });
  };

  private readonly handleSubmitThresholdRequest = async (): Promise<void> => {
    const issue = this.state.thresholdIssue;
    if (!issue?.iti) return;

    this.setState({ thresholdLoading: true, thresholdErrorMessage: '' });

    try {
      const result = await requestBieGovernanceDecision(
        issue.iti,
        this.state.thresholdReason.trim(),
        this.state.thresholdEvidence.trim(),
      );

      const action = String(result?.nextaction?.action || '');

      this.setState({
        thresholdDialogOpen: false,
        thresholdIssue: null,
        thresholdReason: '',
        thresholdEvidence: '',
        thresholdLoading: false,
      });

      if (action === 'REVALIDATE') {
        await this.handleOpenValidationDialog();
      } else {
        await this.loadWorkbench();
      }

      this.props.showNotification?.(
        action === 'AWAIT_APPROVAL'
          ? 'Governance Decision requested and is awaiting independent approval.'
          : 'Governance Decision approved by policy and onboarding revalidated.',
        'success',
      );
    } catch (error) {
      this.setState({
        thresholdLoading: false,
        thresholdErrorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly handleOpenThresholdDriver = (issue: IBieOnboardingValidationIssue): void => {
    const driverId = Number(issue.vdd?.driverId ?? issue.vdd?.driverid ?? 0);

    if (!driverId) {
      this.props.showNotification?.(
        'The Driver could not be identified from this validation issue.',
        'warning',
      );
      return;
    }

    this.props.onParam('set')('rid', driverId);
    this.props.setPage('biedriver');
  };

  private readonly handleOpenThresholdRecord = (issue: IBieOnboardingValidationIssue): void => {
    if (issue.iti) this.handleOpenIssueRecord(issue.iti, issue);
  };

  /* ==========================================================
   * Governance Decision handlers
   * ==========================================================
   */

  private async revalidateAfterGovernanceApproval(
    successMessage: string,
  ): Promise<void> {
    await this.refreshValidationState();

    if (!this.mounted) {
      return;
    }

    this.setState({
      thresholdDialogOpen: false,
      thresholdIssue: null,
      thresholdReason: '',
      thresholdEvidence: '',
      thresholdLoading: false,
      thresholdErrorMessage: '',
      successMessage,
    });

    this.props.showNotification?.(
      successMessage,
      'success',
    );
  }
  /* ---------------------------------------------------------------------------
   * 3. Governance Decision: REVIEW HANDLER
   *
   * For P0 a simple notes prompt can be replaced by your standard dialog.
   * ---------------------------------------------------------------------------
   */
  private readonly handleReviewGovernanceDecision = async (
    decision: IBieGovernanceDecision,
    action: BieGovernanceReviewAction,
  ): Promise<void> => {
    if (!decision.rid || !decision.stp) return;

    const notes = window.prompt(
      action === 'APPROVE'
        ? 'Approval notes'
        : action === 'REJECT'
          ? 'Rejection reason'
          : 'Review notes',
      '',
    );

    if (notes === null) return;

    if (
      (action === 'APPROVE' || action === 'REJECT')
      && !notes.trim()
    ) {
      this.props.showNotification?.(
        'Review notes are required.',
        'warning',
      );
      return;
    }

    try {
      const result =
        await reviewBieGovernanceDecision(
          decision.rid,
          action,
          notes.trim(),
          decision.stp,
        );

      await this.loadGovernanceDecisions();

      const nextAction = String(
        result?.nextaction?.action || '',
      ).toUpperCase();

      if (nextAction === 'REVALIDATE') {
        await this.revalidateAfterGovernanceApproval(
          'Governance Decision approved. Validation has been refreshed.'
        );
      }
    } catch (error) {
      this.setState({
        errorMessage: this.getErrorMessage(error),
      });
    }
  };


  /* ---------------------------------------------------------------------------
   * 4. Governance Decision: WITHDRAW HANDLER
   * ---------------------------------------------------------------------------
   */
  private readonly handleWithdrawGovernanceDecision = async (
    decision: IBieGovernanceDecision,
  ): Promise<void> => {
    if (!decision.rid || !decision.stp) return;

    const reason = window.prompt(
      'Reason for withdrawing this Governance Decision',
      '',
    );

    if (reason === null) return;

    try {
      await withdrawBieGovernanceDecision(
        decision.rid,
        reason.trim(),
        decision.stp,
      );

      await this.loadGovernanceDecisions();
      await this.loadWorkbench(false);
    } catch (error) {
      this.setState({
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  /* ---------------------------------------------------------------------------
   * 5. Governance Decision: OPEN DRIVER
   * ---------------------------------------------------------------------------
   */
  private readonly handleOpenGovernanceDriver0 = (
    decision: IBieGovernanceDecision,
  ): void => {
    if (!decision.dri) return;

    this.props.onParam('set')('rid', decision.dri);
    this.props.onParam('set')(
      'returnpage',
      'bieonboardingworkbench',
    );

    this.props.setPage('biedriver');
  };


  /* ==========================================================
   * Rule loading
   * ==========================================================
   */

  private async loadValidationRules(): Promise<void> {
    const requestVersion = ++this.ruleRequestVersion;

    this.safeSetState({
      rulesLoading: true,
      errorMessage: '',
    });

    try {
      //const rules = await this.api.findValidationRules({
      const rules = await fetchBieOnboardingValidationRules({
        status: 1,
      });

      if (!this.mounted || requestVersion !== this.ruleRequestVersion) {
        return;
      }

      this.setState({
        rules,
        rulesLoading: false,
        errorMessage: '',
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.ruleRequestVersion) {
        return;
      }

      this.setState({
        rulesLoading: false,

        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  /* ==========================================================
   * Validation
   * ==========================================================
   */

  private readonly handleOpenValidationDialog = (): void => {
    const workbench = this.state.workbench;

    if (!workbench) {
      return;
    }

    if (!workbench.actions.canValidate && !workbench.actions.canRevalidate) {
      this.setState({
        errorMessage:
          'The current Load Session is not available for validation.',
      });

      return;
    }

    this.setState({
      validationDialogOpen: true,
    });
  };

  private readonly handleCloseValidationDialog = (): void => {
    if (this.state.validating) {
      return;
    }

    this.setState({
      validationDialogOpen: false,
    });
  };

  // private readonly handleConfirmValidation0 = async (): Promise<void> => {
  //   const workbench = this.state.workbench;

  //   if (!workbench) {
  //     return;
  //   }

  //   this.setState({
  //     validating: true,
  //     errorMessage: '',
  //     successMessage: '',
  //   });

  //   try {
  //     //const result = await this.api.validateWorkbook(this.props.loadSessionId, {
  //     const result = await validateBieOnboardingWorkbook(this.props.params?.loadSessionId,workbench.loadSession.stamp );

  //     if (!this.mounted) {
  //       return;
  //     }

  //     const nextWorkbench = result.workbench;
  //     const rejected = String(result.validation.state).toUpperCase() === 'REJECTED';

  //     this.setState(
  //       {
  //         workbench: nextWorkbench,
  //         validating: false,
  //         validationDialogOpen: false,
  //         successMessage: rejected
  //           ? 'Validation completed. Blocking issues require correction.'
  //           : 'The onboarding workbook passed validation.',
  //         errorMessage: '',
  //         selectedTab: rejected ? 'ISSUES' : 'OVERVIEW',
  //         filters: rejected
  //           ? {
  //               ...emptyBieOnboardingWorkbenchFilters,
  //               blocking: '1',
  //             }
  //           : {
  //               ...this.state.filters,
  //             },

  //         issuePageOffset: 0,
  //         issues: rejected ? this.state.issues : [],
  //         selectedIssueId: '',
  //       },
  //       () => {
  //         this.props.onWorkbenchChange?.(nextWorkbench);
  //         if (rejected) {
  //           void this.loadValidationIssues();
  //         }
  //       }
  //     );
  //   } catch (error) {
  //     if (!this.mounted) {
  //       return;
  //     }

  //     this.setState({
  //       validating: false,
  //       validationDialogOpen: false,

  //       errorMessage: this.getErrorMessage(error),
  //     });
  //   }
  // };

  // Inside BieOnboardingWorkbench:
  private handleConfirmValidation = async (): Promise<void> => {
    const loadSessionId = this.resolveLoadSessionId();
    const stamp =
      this.state.workbench?.loadSession?.stamp ?? this.props.params?.stp;

    if (!loadSessionId || !stamp) {

      if(this.props.showNotification){
        this.props.showNotification('A valid Load Session and concurrency stamp are required','error');
      }

      this.setState({
        errorMessage:
          'A valid Load Session and concurrency stamp are required.',
      });
      return;
    }

    //
    const workbench = this.state.workbench;
    if (!workbench) {
      return;
    }

    this.setState({
      validating: true,
      errorMessage: '',
      successMessage: '',
    });

    try {
      const result = await validateBieOnboardingReview(loadSessionId, stamp);
      if (!this.mounted) {
        return;
      }
      // The returned Workbench replaces every display area affected by validation:
      // loadSession, actions, summary, imports, recentIssues and lookups.
      this.setState({
        validating: false,
        workbench: result.workbench,
      });

      // Preserve the new concurrency stamp for subsequent first-class page actions.
      this.props.onParam('set')('stp', result.validation.stamp);

      if (this.props.showNotification) {
        this.props.showNotification(result.validation.message, result.validation.valid ? 'success' : 'warning');
      }
      // this.props.showNotification?.(
      //   result.validation.message,
      //   result.validation.valid ? 'success' : 'warning',
      // );
      const nextWorkbench = result.workbench;
      const rejected =
        String(result.validation.state).toUpperCase() === 'REJECTED';

      this.setState(
        {
          workbench: nextWorkbench,
          validating: false,
          validationDialogOpen: false,
          successMessage: rejected
            ? 'Validation completed. Blocking issues require correction.'
            : 'The onboarding workbook passed validation.',
          errorMessage: '',
          selectedTab: rejected ? 'ISSUES' : 'OVERVIEW',
          filters: rejected
            ? {
                ...emptyBieOnboardingWorkbenchFilters,
                blocking: '1',
              }
            : {
                ...this.state.filters,
              },

          issuePageOffset: 0,
          issues: rejected ? this.state.issues : [],
          selectedIssueId: '',
        },
        () => {
          this.props.onWorkbenchChange?.(nextWorkbench);
          if (rejected) {
            void this.loadValidationIssues();
          }
        }
      );
    } catch (error) {
      if (!this.mounted) {
        return;
      }

      this.setState({
        validating: false,
        validationDialogOpen: false,

        errorMessage: this.getErrorMessage(error),
      });
      //this.setState({ validating: false });

      
      // this.props.showNotification?.(
      //   error instanceof Error ? error.message : 'Review validation failed.',
      //   'error',
      // );
      if (this.props.showNotification) {
        this.props.showNotification('A valid Load Session and concurrency stamp are required', 'error');
      }
    }
  };

  // Inside BieOnboardingWorkbench:
  // private validateReview = async (): Promise<void> => {
  //   const loadSessionId = this.resolveLoadSessionId();
  //   const stamp =
  //     this.state.workbench?.loadSession?.stamp ?? this.props.params?.stp;

  //   if (!loadSessionId || !stamp) {
  //     // this.props.showNotification?(
  //     //   'A valid Load Session and concurrency stamp are required.',
  //     //   'error',
  //     // );
  //     return;
  //   }

  //   this.setState({ validating: true });

  //   try {
  //     const result = await validateBieOnboardingReview(loadSessionId, stamp);

  //     // The returned Workbench replaces every display area affected by validation:
  //     // loadSession, actions, summary, imports, recentIssues and lookups.
  //     this.setState({
  //       validating: false,
  //       workbench: result.workbench,
  //     });

  //     // Preserve the new concurrency stamp for subsequent first-class page actions.
  //     this.props.onParam('set')('stp', result.validation.stamp);

  //     // this.props.showNotification?.(
  //     //   result.validation.message,
  //     //   result.validation.valid ? 'success' : 'warning',
  //     // );
  //   } catch (error) {
  //     this.setState({ validating: false });

  //     // this.props.showNotification?.(
  //     //   error instanceof Error ? error.message : 'Review validation failed.',
  //     //   'error',
  //     // );
  //   }
  // };

  private renderValidationDialog(): React.ReactNode {
    const workbench = this.state.workbench;

    const revalidation = Boolean(workbench?.actions.canRevalidate);

    return (
      <Dialog
        open={this.state.validationDialogOpen}
        onClose={this.handleCloseValidationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {revalidation
            ? 'Revalidate Onboarding Workbook'
            : 'Validate Onboarding Workbook'}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {revalidation
              ? 'The validation engine will clear the previous ' +
                'validation findings, resolve the corrected staged ' +
                'records and apply all active rules again.'
              : 'The validation engine will resolve controlled ' +
                'references and apply all active business, period, ' +
                'unit and reconciliation rules.'}
          </DialogContentText>

          {workbench && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
              }}
            >
              {this.formatNumber(workbench.summary.items.total)}
              {' staged record(s) across '}
              {this.formatNumber(workbench.imports.length)}
              {' workbook import(s) will be evaluated.'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            color="inherit"
            disabled={this.state.validating}
            onClick={this.handleCloseValidationDialog}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color={revalidation ? 'warning' : 'primary'}
            disabled={this.state.validating}
            startIcon={
              this.state.validating ? (
                <CircularProgress size={18} />
              ) : (
                <FactCheckOutlinedIcon />
              )
            }
            onClick={this.handleConfirmValidation}
          >
            {this.state.validating
              ? 'Validating…'
              : revalidation
                ? 'Revalidate'
                : 'Validate'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* ==========================================================
   * Publication callback
   * ==========================================================
   */

  private readonly handlePublish = async (): Promise<void> => {
    const workbench = this.state.workbench;

    if (!workbench || !workbench.actions.canPublish) {
      return;
    }

    this.setState({
      publishing: true,
      errorMessage: '',
      successMessage: '',
      selectedTab: 'PUBLISH',
    });

    try {
      let publicationResult: IBieLoadSessionPublishResult | null = null;

      if (this.props.onPublish) {
        await this.props.onPublish(
          this.state.loadSessionId,
          workbench.loadSession.stamp,
        );
      } else {
        publicationResult = await publishBieLoadSession({
          loadSessionId: Number(this.state.loadSessionId),
          stamp: workbench.loadSession.stamp,
        });
      }

      if (!this.mounted) {
        return;
      }

      this.setState({
        publishing: false,
        successMessage: 'The onboarding Load Session was published successfully.',
        selectedTab: 'PUBLISH',
        publicationResult,
      });

      await this.loadWorkbench(false);
    } catch (error) {
      if (!this.mounted) {
        return;
      }

      this.setState({
        publishing: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly handleExitToPlanningCycle = (): void => {
    const workbench = this.state.workbench;

    if (!workbench || String(workbench.loadSession.stateCode).toUpperCase() !== 'PUBLISHED') {
      return;
    }

    const setParam = this.props.onParam('set');
    setParam('loadsessionid', workbench.loadSession.rid);
    setParam('loadsessionix', workbench.loadSession.rix);
    this.props.setPage('planningcycle');
  };

  /* ==========================================================
   * Tab handlers
   * ==========================================================
   */

  private readonly handleTabChange = (
    _event: React.SyntheticEvent,
    value: BieOnboardingWorkbenchTab
  ): void => {
    this.setState(
      {
        selectedTab: value,
        errorMessage: '',
      },
      () => {
        if (value === 'ISSUES' && this.state.issues.length === 0) {
          void this.loadValidationIssues();
        }

        if (value === 'RULES' && this.state.rules.length === 0) {
          void this.loadValidationRules();
        }

        if (value === 'STAGING' && !this.state.stagingReview && !this.state.loadingStagingReview) {
          void this.loadStagingReview(); 
        }
      }
    );
  };
  
  private readonly handleViewAllIssues = (): void => {
    this.setState(
      {
        selectedTab: 'ISSUES',

        filters: {
          ...emptyBieOnboardingWorkbenchFilters,
        },

        issuePageOffset: 0,
        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  /* ==========================================================
   * Staging handlers
   * ==========================================================
   */

  private readonly handleRefreshStagingReview =
    (): void => {
      void this.loadStagingReview(true);
    };

  private readonly handleOpenStagedImportRecords =
    (
      importId: BieRecordId,
      item: IBieOnboardingStagedImportSummary,
    ): void => {
      const loadSessionId =
        this.resolveLoadSessionId();

      if (!loadSessionId || !importId) {
        this.setState({
          stagingErrorMessage:
            'The Load Session and Import are required.',
        });

        return;
      }

      const setImportRecordsParam =
        this.props.onParam(
          'set',
        );

      setImportRecordsParam(
        'loadsessionid',
        loadSessionId,
      );

      setImportRecordsParam(
        'importid',
        importId,
      );

      setImportRecordsParam(
        'importclasscode',
        item.classCode,
      );

      setImportRecordsParam(
        'sheetname',
        item.sheetName,
      );

      setImportRecordsParam(
        'returnpage',
        'bieonboardingworkbench',
      );

      setImportRecordsParam(
        'returntab',
        'staging',
      );

      this.props.setPage(
        PAGE_IMPORT_RECORDS,
      );
    };

  private readonly handleCompleteStaging = async (): Promise<void> => {
      const workbench = this.state.workbench;

      if (!workbench) {
        return;
      }

      const loadSessionId = workbench.loadSession.rid; //loadSessionId;

      const stamp = workbench.loadSession.stamp;

      if (!loadSessionId) {
        // this.props.showNotification(
        //   'error',
        //   'Load Session ID is required.',
        // );
        if (this.props.showNotification) {
          this.props.showNotification('Load Session ID is required.', 'error');
        }

        return;
      }

      if (!stamp) {
        // this.props.showNotification(
        //   'error',
        //   'Load Session concurrency stamp is required.',
        // );
        if (this.props.showNotification) {
          this.props.showNotification('Load Session concurrency stamp is required.', 'error');
        }

        return;
      }

      this.setState({
        staging: true,
      });

      try {
        const result = await stageBieLoadSession({loadSessionId:Number(loadSessionId),stamp});

        // this.props.showNotification(
        //   'success',
        //   `Load Session staged successfully. ${result.importCount} active import${result.importCount === 1 ? '' : 's'} confirmed.`,
        // );
        if (this.props.showNotification) {
          const c = result.importCount === 1 ? '' : 's'
          const notmessage = `Load Session staged successfully. ${result.importCount} active import${c} confirmed.`
          this.props.showNotification(notmessage, 'success');
        }

        await this.loadWorkbench();

        await this.loadStagingReview(true);
      } catch (error) {
        // this.props.showNotification(
        //   'error',
        //   this.getErrorMessage(
        //     error,
        //   ),
        // );
        if (this.props.showNotification) {
          this.props.showNotification(this.getErrorMessage(error), 'error');
        }
      } finally {
        if (this.mounted) {
          this.setState({
            staging: false,
          });
        }
      }
    };

  private readonly handleResetStaging = async (): Promise<void> => {
    const workbench = this.state.workbench;
    if (!workbench) return;

    this.setState({
      resettingStaging: true,
      errorMessage: '',
    });

    try {
      const result = await resetBieOnboardingStaging(
        workbench.loadSession.rid,
        workbench.loadSession.stamp,
      );

      this.props.onParam('set')('adminresolution', null);

      this.setState({
        resetDialogOpen: false,
        resettingStaging: false,
        governanceDecisions: [],
        selectedDuplicateItemIds: [],
        issues: [],
      });

      await this.loadWorkbench(true);
      //await this.loadGovernanceDecisions();

      this.props.showNotification?.(
        result?.nextaction?.instruction
        || 'Staging reset successfully. Upload the corrected workbook.',
        'success',
      );
    } catch (error) {
      this.setState({
        resettingStaging: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  /* ==========================================================
   * reopen handlers
   * ==========================================================
   */

  private readonly handleReopenForCorrection =
    async (): Promise<void> => {
      const loadSessionId = this.resolveLoadSessionId();

      const stamp =
        this.state.workbench?.loadSession?.stamp ||
        String(this.props.params?.stp || '');

      if (!loadSessionId || !stamp) {
        if (this.props.showNotification){
          this.props.showNotification(
            'A valid Load Session and concurrency stamp are required.',
            'error',
          );
        }
        return;
      }

      this.setState({
        reopening: true,
      });

      try {
        const result = await reopenBieLoadSession({
          loadSessionId: Number(loadSessionId),
          stamp,
        });

        const setWizardParam =
          this.props.onParam(PAGE_LOAD_SESSION_WIZARD);

        setWizardParam('mode', 'EDIT');
        setWizardParam('loadsessionid', loadSessionId);
        setWizardParam('stp', result.stamp);

        if (this.props.showNotification){
          this.props.showNotification(
            'Load Session reopened. Reset Staging before uploading the corrected workbook.',
            'success',
          );
        }

        this.props.setPage(PAGE_LOAD_SESSION_WIZARD);
      } catch (error) {
        if (this.props.showNotification){
          this.props.showNotification(
            error instanceof Error
              ? error.message
              : 'The Load Session could not be reopened.',
            'error',
          );
        }

        this.setState({
          reopening: false,
        });
      }
    };

  /* ==========================================================
   * Import handlers
   * ==========================================================
   */

  private readonly handleSelectImport = (importId: BieRecordId): void => {
    this.setState({
      selectedImportId: importId,
    });
  };

  private readonly handleViewImportIssues = (
    importId: BieRecordId,
    _data: IBieOnboardingWorkbenchImport
  ): void => {
    this.setState(
      {
        selectedImportId: importId,

        selectedTab: 'ISSUES',

        filters: {
          ...emptyBieOnboardingWorkbenchFilters,

          importId,
        },

        issuePageOffset: 0,

        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  private readonly handleViewImportRecords = (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ): void => {
    this.setState({
      selectedImportId: importId,
    });

    if (this.props.onOpenImportRecords) {
      this.props.onOpenImportRecords(importId, data);
      return;
    }

    const setParam = this.props.onParam('set');
    const unsetParam = this.props.onParam('unset');
    unsetParam('importitemid', null);
    setParam('loadsessionid', this.resolveLoadSessionId());
    setParam('importid', importId);
    setParam('importcode', data.code || null);
    setParam('importname', data.name || null);
    setParam('sheetname', data.sheetName || null);
    setParam('classcode', data.classCode || null);
    setParam('stp', this.state.workbench?.loadSession.stamp || null);
    this.props.setPage(PAGE_IMPORT_RECORDS);
  };

  /* ==========================================================
   * Issue handlers
   * ==========================================================
   */

  private readonly handleIssueFiltersChange = (
    filters: IBieOnboardingWorkbenchFilters
  ): void => {
    this.setState({
      filters,
    });
  };

  private readonly handleApplyIssueFilters = (): void => {
    this.setState(
      {
        issuePageOffset: 0,
        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  private readonly handleClearIssueFilters = (): void => {
    this.setState(
      {
        filters: {
          ...emptyBieOnboardingWorkbenchFilters,
        },

        issuePageOffset: 0,
        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  private readonly handleRefreshIssues = (): void => {
    void this.loadValidationIssues();
  };

  private readonly handleIssuePageChange = (pageOffset: number): void => {
    this.setState(
      {
        issuePageOffset: pageOffset,

        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  private readonly handleIssuePageLimitChange = (pageLimit: number): void => {
    this.setState(
      {
        issuePageLimit: pageLimit,

        issuePageOffset: 0,

        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  private readonly handleSelectIssue = (
    issue: IBieOnboardingValidationIssue
  ): void => {
    this.setState({
      selectedIssueId: issue.rid,
    });
  };

  private readonly handleOpenIssueRecord = (
    importItemId: BieRecordId,
    issue: IBieOnboardingValidationIssue
  ): void => {
    this.setState({
      selectedIssueId: issue.rid,
    });

    if (this.props.onOpenImportItem) {
      this.props.onOpenImportItem(importItemId, issue);
      return;
    }

    const setParam = this.props.onParam('set');
    setParam('loadsessionid', this.resolveLoadSessionId());
    setParam('importid', issue.imi || this.state.selectedImportId || null);
    setParam('importitemid', importItemId);
    setParam('stp', this.state.workbench?.loadSession.stamp || null);
    this.props.setPage(PAGE_IMPORT_ITEM);
  };

  // 3. Direct UNIT_MISMATCH routing:
  //
  private readonly handleOpenUnitAlias = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    const details = issue.vdd || {};

    const driverId = Number(details.driverId ?? 0);
    const canonicalUnitId = Number(details.canonicalUnitId ?? 0);
    const aliasCode = String(details.suppliedUnitCode ?? issue.src ?? '');
    const sourceId = Number(details.sourceId ?? details.sourceid ?? 0);

    this.props.onParam('set')('returnpage', 'bieonboarding');
    this.props.onParam('set')('adminresolution', 1);
    this.props.onParam('set')('shc', aliasCode || null);
    this.props.onParam('set')('uni', canonicalUnitId || null);
    this.props.onParam('set')('dri', driverId || null);
    this.props.onParam('set')('soi', sourceId || null);
    this.props.onParam('set')('ovr', 1);
    this.props.onParam('set')('dft', 1);

    this.props.setPage(PAGE_UNIT_ALIAS);
  };

  private readonly handleOpenProductAlias = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    const details = issue.vdd || {};

    this.props.onParam('set')('returnpage', 'bieonboardingworkbench');
    this.props.onParam('set')('lsi', this.state.workbench?.loadSession?.rid || null);
    this.props.onParam('set')('adminresolution', 1);
    this.props.onParam('set')('shc', details.suppliedUnitCode || issue.src || null);
    this.props.onParam('set')('uni', details.canonicalUnitId || null);
    this.props.onParam('set')('unc', details.canonicalUnitCode || null);
    this.props.onParam('set')('soi', details.sourceId ?? details.sourceid ?? null);
    this.props.onParam('set')('ovr', 1);

    this.props.setPage(PAGE_PRODUCT_ALIAS);
  };

  private readonly handleOpenLocationAlias = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    const details = issue.vdd || {};

    this.props.onParam('set')('returnpage', 'bieonboardingworkbench');
    this.props.onParam('set')('lsi', this.state.workbench?.loadSession?.rid || null);
    this.props.onParam('set')('adminresolution', 1);
    this.props.onParam('set')('shc', details.suppliedUnitCode || issue.src || null);
    this.props.onParam('set')('uni', details.canonicalUnitId || null);
    this.props.onParam('set')('unc', details.canonicalUnitCode || null);
    this.props.onParam('set')('soi', details.sourceId ?? details.sourceid ?? null);
    this.props.onParam('set')('ovr', 1);

    this.props.setPage(PAGE_LOCATION_ALIAS);
  };

  private readonly handleOpenProduct = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    const details = issue.vdd || {};

    this.props.onParam('set')('returnpage', 'bieonboardingworkbench');
    this.props.onParam('set')('lsi', this.state.workbench?.loadSession?.rid || null);
    this.props.onParam('set')('adminresolution', 1);
    this.props.onParam('set')('shc', details.suppliedUnitCode || issue.src || null);
    this.props.onParam('set')('uni', details.canonicalUnitId || null);
    this.props.onParam('set')('unc', details.canonicalUnitCode || null);
    this.props.onParam('set')('soi', details.sourceId ?? details.sourceid ?? null);
    this.props.onParam('set')('ovr', 1);

    this.props.setPage(PAGE_PRODUCT);
  };

  private readonly handleOpenLocation = (
    issue: IBieOnboardingValidationIssue,
  ): void => {
    const details = issue.vdd || {};

    this.props.onParam('set')('returnpage', 'bieonboardingworkbench');
    this.props.onParam('set')('lsi', this.state.workbench?.loadSession?.rid || null);
    this.props.onParam('set')('adminresolution', 1);
    this.props.onParam('set')('shc', details.suppliedUnitCode || issue.src || null);
    this.props.onParam('set')('uni', details.canonicalUnitId || null);
    this.props.onParam('set')('unc', details.canonicalUnitCode || null);
    this.props.onParam('set')('soi', details.sourceId ?? details.sourceid ?? null);
    this.props.onParam('set')('ovr', 1);

    this.props.setPage(PAGE_LOCATION);
  };

  // 4. Generic remediation dispatcher:
  //
  private readonly handleIssueRemediation = (
    action: IBieRenderedRemediation,
    issue: IBieOnboardingValidationIssue,
  ): void => {
    switch (action.code) {
      case 'COMPARE_RESOLVE':
        this.handleOpenDecisionDialog(issue);
        return;

      case 'OPEN_UNIT_ALIAS':
        this.props.onParam('set')('adminresolution', 1);
        this.handleOpenUnitAlias(issue);
        return;

      case 'OPEN_DRIVER':
      case 'OPEN_DRIVER_THRESHOLD':
        this.props.onParam('set')('adminresolution', 1);
        this.handleOpenThresholdDriver(issue);
        return;

      case 'OPEN_PRODUCT_ALIAS':
        this.props.onParam('set')('adminresolution', 1);
        this.handleOpenProductAlias(issue);
        return;

      case 'OPEN_LOCATION_ALIAS':
        this.props.onParam('set')('adminresolution', 1);
        this.handleOpenLocationAlias(issue);
        return;

      case 'OPEN_PRODUCT':
        this.props.onParam('set')('adminresolution', 1);
        this.handleOpenProduct(issue);
        return;

      case 'OPEN_LOCATION':
        this.props.onParam('set')('adminresolution', 1);
        this.handleOpenLocation(issue);
        return;

      case 'REQUEST_DECISION':
        this.handleOpenThresholdDialog(issue);
        return;

      case 'OPEN_IMPORT_ITEM':
      default:
        this.handleOpenIssueRecord(issue.iti, issue);
        return;
    }
  };

  // private readonly handleIssueRemediation = (
  //   action: IBieRenderedRemediation,
  //   issue: IBieOnboardingValidationIssue,
  // ): void => {
  //   switch (action.code) {
  //     case 'COMPARE_RESOLVE':
  //       this.handleOpenDecisionDialog(issue);
  //       return;

  //     case 'OPEN_UNIT_ALIAS':
  //       this.handleOpenUnitAlias(issue);
  //       return;

  //     case 'OPEN_DRIVER':
  //     case 'OPEN_DRIVER_THRESHOLD':
  //       this.handleOpenThresholdDriver(issue);
  //       return;

  //     case 'REQUEST_DECISION':
  //       this.handleOpenThresholdDialog(issue);
  //       return;

  //     case 'OPEN_IMPORT_ITEM':
  //       this.handleOpenIssueRecord(issue.iti, issue);
  //       return;

  //     default:
  //       this.handleOpenIssueRecord(issue.iti, issue);
  //   }
  // };

  //
  // 5. Resolve + Revalidate:
  //
  private readonly handleResolveAndRevalidate0 = async (): Promise<void> => {
    const loadSession = this.state.workbench?.loadSession;
    if (!loadSession?.rid || !loadSession?.stamp) return;

    this.setState({ validating: true, errorMessage: '' });

    try {
      const result = await resolveAndValidateBieOnboardingReview(
        loadSession.rid,
        loadSession.stamp,
      );

      this.setState({
        workbench: result.workbench,
        validating: false,
      });

      this.props.onParam('set')('adminresolution', null);
      //await this.loadIssues(true);
      await this.loadValidationIssues();
    } catch (error) {
      this.setState({
        validating: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  };

  private readonly handleResolveAndRevalidate = async (): Promise<void> => {
      const loadSession = this.state.workbench?.loadSession;
      if (!loadSession?.rid || !loadSession?.stamp) {
        return;
      }

      this.setState({validating: true,errorMessage: '',});

      try {
        const result = await resolveAndValidateBieOnboardingReview(
          loadSession.rid,
          loadSession.stamp,
        );

        if (!this.mounted) {
          return;
        }

        this.props.onParam('set')('stp',result.validation.stamp);
        this.props.onParam('set')('adminresolution',null);

        this.setState(
          {
            workbench: result.workbench,
            validating: false,
            errorMessage: '',
          },
          () => {
            this.props.onWorkbenchChange?.(result.workbench);
          },
        );

        await this.loadValidationIssues();
        await this.loadGovernanceDecisions();

      } catch (error) {
        this.setState({
          validating: false,
          errorMessage:
            this.getErrorMessage(error),
        });
      }
    };

  // private hasAdministrativeResolutionPending = (): boolean =>
  //   Number(this.props.params?.adminresolution || 0) === 1;
  private hasAdministrativeResolutionPending = (): boolean => {
    if (Number(this.props.params?.adminresolution || 0) === 1) {
      return true;
    }

    const workbench = this.state.workbench;

    const stateCode =
      String(workbench?.loadSession?.stateCode || '').toUpperCase();

    const importCount =
      Number(workbench?.summary?.imports?.total || 0);

    return (
      stateCode === 'STAGED'
      && importCount > 0
    );
  };

  /* ==========================================================
   * Rule handlers
   * ==========================================================
   */

  private readonly handleRefreshRules = (): void => {
    void this.loadValidationRules();
  };

  private readonly handleSelectRule = (
    rule: IBieOnboardingValidationRule
  ): void => {
    this.setState({
      selectedRuleId: rule.rid,
    });
  };

  private readonly handleViewRuleIssues = (
    ruleCode: string,
    rule: IBieOnboardingValidationRule
  ): void => {
    this.setState(
      {
        selectedRuleId: rule.rid,

        selectedTab: 'ISSUES',

        filters: {
          ...emptyBieOnboardingWorkbenchFilters,

          ruleCode,
        },

        issuePageOffset: 0,

        selectedIssueId: '',
      },
      () => {
        void this.loadValidationIssues();
      }
    );
  };

  /* ==========================================================
   * Refresh
   * ==========================================================
   */

  private readonly handleRefreshWorkbench = (): void => {
    void this.loadWorkbench(false);
  };

  /* ==========================================================
   * Notifications
   * ==========================================================
   */

  private renderNotifications(): React.ReactNode {
    return (
      <>
        <Snackbar
          open={Boolean(this.state.successMessage)}
          autoHideDuration={6000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          onClose={this.handleCloseSuccessMessage}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={this.handleCloseSuccessMessage}
            sx={{
              width: '100%',
            }}
          >
            {this.state.successMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={
            Boolean(this.state.errorMessage) &&
            this.state.selectedTab !== 'OVERVIEW' &&
            this.state.selectedTab !== 'ISSUES' &&
            this.state.selectedTab !== 'RULES'
          }
          autoHideDuration={8000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          onClose={this.handleCloseErrorMessage}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={this.handleCloseErrorMessage}
            sx={{
              width: '100%',
            }}
          >
            {this.state.errorMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }

  private readonly handleCloseSuccessMessage = (): void => {
    this.setState({
      successMessage: '',
    });
  };

  private readonly handleCloseErrorMessage = (): void => {
    this.setState({
      errorMessage: '',
    });
  };

  /* ==========================================================
   * State reset
   * ==========================================================
   */

  private resetForLoadSessionChange(loadSessionId: BieRecordId): void {
    this.workbenchRequestVersion += 1;
    this.issueRequestVersion += 1;
    this.ruleRequestVersion += 1;

    this.setState({
      ...emptyBieOnboardingWorkbenchState,

      issuesLoading: false,
      rulesLoading: false,
      publishing: false,
      publicationResult: null,

      validationDialogOpen: false,

      selectedImportId: '',
      selectedIssueId: '',
      selectedRuleId: '',
      loadSessionId,
    });
  }

  private async refreshValidationState(): Promise<
    IBieOnboardingReviewValidateResult
  > {
    const loadSessionId = this.resolveLoadSessionId();

    const stamp =
      this.state.workbench?.loadSession?.stamp
      ?? this.props.params?.stp;

    if (!loadSessionId || !stamp) {
      throw new Error(
        'A valid Load Session and concurrency stamp are required.'
      );
    }

    const result = await validateBieOnboardingReview(
      loadSessionId,
      stamp,
    );

    if (!this.mounted) {
      return result;
    }

    this.props.onParam('set')(
      'stp',
      result.validation.stamp,
    );

    this.setState({
      workbench: result.workbench,
      issuePageOffset: 0,
      errorMessage: '',
    });

    this.props.onWorkbenchChange?.(
      result.workbench,
    );

    await this.loadValidationIssues();
    await this.loadGovernanceDecisions();

    return result;
  }
  /* ==========================================================
   * Generic display states
   * ==========================================================
   */

  private renderTabLoading(message: string): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
        }}
      >
        <CircularProgress />

        <Typography
          variant="body1"
          fontWeight={600}
          sx={{
            mt: 2,
          }}
        >
          {message}
        </Typography>
      </Paper>
    );
  }

  private renderTabUnavailable(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
        }}
      >
        <AssessmentOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 52,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          Workbench data unavailable
        </Typography>

        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          sx={{
            mt: 2,
          }}
          onClick={this.handleRefreshWorkbench}
        >
          Retry
        </Button>
      </Paper>
    );
  }

  /* ==========================================================
   * Helpers
   * ==========================================================
   */

  private safeSetState(
    state: Partial<IBieOnboardingWorkbenchComponentState>
  ): void {
    if (!this.mounted) {
      return;
    }

    this.setState(
      state as Pick<
        IBieOnboardingWorkbenchComponentState,
        keyof IBieOnboardingWorkbenchComponentState
      >
    );
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof BieOnboardingApiError) {
      if (error.statusCode === 409) {
        return (
          'The Load Session was changed by another operation. ' +
          'Refresh the Workbench and try again.'
        );
      }

      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return (
      'An unexpected error occurred while processing the ' +
      'Onboarding Workbench request.'
    );
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return new Intl.NumberFormat().format(numericValue);
  }

  private readonly getCurrentUserId = (): number => {
    const userData = this.props.userData as any;

    return Number(
      userData?.rid
      ?? userData?.recid
      ?? userData?.usi
      ?? 0
    );
  };

  private readonly getFilteredGovernanceDecisions =
    (): IBieGovernanceDecision[] => {

      const filter =
        String(this.state.governanceStateFilter || 'ALL')
          .toUpperCase();

      if (filter === 'ALL') {
        return this.state.governanceDecisions;
      }

      if (filter === 'PENDING') {
        return this.state.governanceDecisions.filter(
          decision =>
            decision.ssc === 'REQUESTED'
            || decision.ssc === 'UNDER_REVIEW'
        );
      }

      return this.state.governanceDecisions.filter(
        decision =>
          String(decision.ssc || '').toUpperCase() === filter
      );
    };


  private readonly getSelectedGovernanceDecision =
    (): IBieGovernanceDecision | null => {

      const selectedId =
        this.state.selectedGovernanceDecisionId;

      if (!selectedId) {
        return null;
      }

      return (
        this.state.governanceDecisions.find(
          decision =>
            String(decision.rid) === String(selectedId)
        )
        ?? null
      );
    };


  private readonly getGovernanceStatusColor = (
    stateCode?: string,
  ):
    | 'default'
    | 'warning'
    | 'info'
    | 'success'
    | 'error' => {

    switch (String(stateCode || '').toUpperCase()) {
      case 'REQUESTED':
        return 'warning';

      case 'UNDER_REVIEW':
        return 'info';

      case 'APPROVED':
        return 'success';

      case 'REJECTED':
        return 'error';

      default:
        return 'default';
    }
  };


  private readonly formatGovernanceAmount = (
    value?: number | null,
  ): string => {

    if (value === null || value === undefined) {
      return '—';
    }

    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
      return '—';
    }

    return numeric.toLocaleString(
      undefined,
      {
        maximumFractionDigits: 6,
      }
    );
  };

  // ---------------------------------------------------------------------------
  // 1. CAN RESET
  // ---------------------------------------------------------------------------

  private canResetStaging(): boolean {
    const workbench = this.state.workbench;
    if (!workbench) return false;

    const stateCode =
      String(workbench.loadSession.stateCode || '').toUpperCase();

    return (
      Boolean(workbench.actions.canClear)
      && ['DRAFT', 'STAGED', 'REJECTED'].includes(stateCode)
      && workbench.imports.length > 0
    );
  }

  private readonly canReopenForCorrection = (): boolean => {
    const loadSession = this.state.workbench?.loadSession;

    if (!loadSession) {
      return false;
    }

    const stateCode =
      String(loadSession.stateCode || '').toUpperCase();

    return (
      stateCode === 'REJECTED'
      || stateCode === 'FAILED'
      || (
        stateCode === 'VALIDATED'
        && !loadSession.publishedAt
      )
    );
  };
}
